import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { getResponseMessage } from '@app/shared/utils/response-message.util';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private readonly http: HttpClient) { }

  private readonly apiUrl = environment.apiurl;

  private setHeaders(): HttpHeaders {
    const headersConfig = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Cors-Policy': environment.corsPolicy
    };
    return new HttpHeaders(headersConfig);
  }

  private formatErrors = (error: any): Observable<never> => {
    const userMessage = getResponseMessage(
      error,
      'Ocorreu um erro ao comunicar com o servidor. Por favor, tente novamente.'
    );

    if (error && typeof error === 'object') {
      try {
        error.userMessage = userMessage;
        Object.defineProperty(error, 'message', {
          value: userMessage,
          writable: true,
          configurable: true,
        });
      } catch {}
      return throwError(() => error);
    }

    return throwError(() => new Error(userMessage));
  };

  private getBaseUrl(path: string): string {
    return `${this.apiUrl}${path}`;
  }

  get<T>(
    path: string,
    httpParams: HttpParams = new HttpParams(),
    responseType: 'json' | 'blob' = 'json'
  ): Observable<T | Blob> {

    if (responseType === 'blob') {
      return this.http.get(this.getBaseUrl(path), {
        headers: this.setHeaders(),
        params: httpParams,
        responseType: 'blob'
      }).pipe(catchError(this.formatErrors));
    }

    return this.http.get<T>(this.getBaseUrl(path), {
      headers: this.setHeaders(),
      params: httpParams,
      responseType: 'json'
    }).pipe(catchError(this.formatErrors));
  }

  put<T>(
    path: string,
    body: Object = {},
    params?: HttpParams,
    customHeaders?: HttpHeaders
  ): Observable<T> {
    const isFormData = body instanceof FormData;
    return this.http
      .put<T>(this.getBaseUrl(path), body, {
        headers: isFormData ? undefined : customHeaders || this.setHeaders(),
        params,
      })
      .pipe(catchError(this.formatErrors));
  }

  post<T>(
    path: string,
    body: Object = {},
    options?: any
  ): Observable<any> {
    const isFormData = body instanceof FormData;

    return this.http
      .post<T>(this.getBaseUrl(path), body, {
        ...options,
        headers: isFormData ? undefined : this.setHeaders(),
      })
      .pipe(catchError(this.formatErrors));
  }

  postQueryParams<T>(
    path: string,
    body: Object = {},
    params?: HttpParams,
    customHeaders?: HttpHeaders
  ): Observable<T> {
    const isFormData = body instanceof FormData;

    return this.http
      .post<T>(this.getBaseUrl(path), body, {
        params,
        headers: isFormData ? undefined : customHeaders || this.setHeaders(),
      })
      .pipe(catchError(this.formatErrors));
  }

  delete<T>(
    path: string,
    httpParams: HttpParams = new HttpParams()
  ): Observable<T> {
    return this.http
      .delete<T>(this.getBaseUrl(path), {
        headers: this.setHeaders(),
        params: httpParams,
      })
      .pipe(catchError(this.formatErrors));
  }

  patch<T>(
    path: string,
    body: Object = {}
  ): Observable<T> {
    return this.http
      .patch<T>(this.getBaseUrl(path), body, {
        headers: this.setHeaders(),
      })
      .pipe(catchError(this.formatErrors));
  }

  public download(
    path: string,
    httpParams: HttpParams = new HttpParams(),
    customHeaders = {}
  ): Observable<Blob> {
    return this.http.get(this.getBaseUrl(path), {
      headers: this.setHeaders(),
      params: httpParams,
      responseType: 'blob',
    });
  }

}
